/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Input,
  message,
  Page,
  Row,
  Select,
  Text,
  Title,
} from 'components';
import styled from 'styled-components';

import { RetsController, RetsOpenHouseController } from '../../../../../api';
import Instructions from './Instructions';

const TriggerRemoveRetsOpenHouse = () => {
  const [listingRef, setListingRef] = useState('');
  const [ohRetsUniqueIds, setOhRetsUniqueIds] = useState('');
  const [removedOpenHouseCount, setRemovedOpenHouseCount] = useState('');
  const [mlsType, setMlsType] = useState<string>('');
  const [mlsTypes, setMlsTypes] = useState<string[]>([]);
  const [loadingMlsTypes, setLoadingMlsTypes] = useState(true);

  const submit = async () => {
    const validListingRef = Boolean(listingRef);
    const validUids = Boolean(ohRetsUniqueIds);

    if (!mlsType) {
      message.error('MLS Type is required to remove open houses');
      return;
    }

    if (!validListingRef || !validUids) {
      message.error(
        'Must provide listingRef and open house retsUniqueIds to remove open houses',
      );
      return;
    }
    try {
      const res = await RetsOpenHouseController.removeOpenHousesByListingRef({
        listingRef,
        mlsType,
        ohUids: ohRetsUniqueIds
          .split(',')
          .map(i => i.trim())
          .join(','),
      });

      setRemovedOpenHouseCount(String(res));
    } catch (e) {
      message.error(
        e?.response?.data?.message ||
          'Could not remove open houses from listing',
      );
    }
  };

  const clear = () => {
    setListingRef('');
    setOhRetsUniqueIds('');
    setRemovedOpenHouseCount('');
  };

  useEffect(() => {
    const fetchMlsTypes = async () => {
      const res = await RetsController.getAllMlsTypes();
      setLoadingMlsTypes(false);
      setMlsTypes(res);
    };
    fetchMlsTypes();
  }, []);

  return (
    <Page type="filled" fillScreenHeight>
      <Layout>
        <Title level={1} hideMargin data-cy="page-title">
          Trigger RETS Remove Open Houses QA Page
        </Title>
        <Text>
          This QA page is used to help trigger the removal of open houses from
          listings, for usage with open house replication feature.
        </Text>
        <Instructions />
        <StyledForm>
          <Row gutter={16}>
            <Col span={24}>
              <Select
                label="Mls Type"
                loading={loadingMlsTypes}
                options={mlsTypes.map(value => ({ label: value, value }))}
                onSelect={setMlsType}
                placeholder="Mls Type"
                required
                showSearch
                value={mlsType}
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Input
                label="Open House retsUniqueIds"
                required
                value={ohRetsUniqueIds}
                onChange={e => {
                  setOhRetsUniqueIds(e.target.value);
                  setRemovedOpenHouseCount('');
                }}
                placeholder="retsUniqueIds, comma separated"
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24}>
              <Input
                label="Open House listingRef"
                required
                value={listingRef}
                onChange={e => {
                  setListingRef(e.target.value);
                  setRemovedOpenHouseCount('');
                }}
                placeholder="listingRef"
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <StyledCol span={24}>
              <Button onClick={submit}>Remove Open Houses</Button>

              <Button onClick={clear}>Clear Form</Button>
            </StyledCol>
          </Row>
          {removedOpenHouseCount && (
            <Alert
              type="success"
              message={
                <>
                  <strong>Result: </strong> {removedOpenHouseCount} open houses
                  were removed from listing with listingRef: {listingRef}
                </>
              }
            />
          )}
        </StyledForm>
      </Layout>
    </Page>
  );
};

export default TriggerRemoveRetsOpenHouse;

const Layout = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 32px;
  padding: 20px;
  background: white;
`;

const StyledForm = styled.form`
  width: 750px;
  display: grid;
  grid-gap: 15px;
`;

const StyledCol = styled(Col)`
  display: flex;
  justify-content: space-between;
`;
